import "@/App.css";
import { lazy, Suspense } from "react";
import { loadBrand } from "@/lib/brand";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import Home from "@/storefront/Home";
import CategoryPage from "@/storefront/CategoryPage";
import BrandPage from "@/storefront/BrandPage";
import BrandsIndex from "@/storefront/BrandsIndex";  // T55 (perf: eager, 4 KB; lazy cost phones a ~1 s chunk round trip before first paint)
const AdminBrandLogoMatches = lazy(() => import("@/pages/AdminBrandLogoMatches"));  // T55
import ProductPage from "@/storefront/ProductPage";
const ClearancePage = lazy(() => import("@/storefront/ClearancePage"));  // T48
import SearchPage from "@/storefront/SearchPage";
const CompareBar = lazy(() => import("@/storefront/CompareBar").then((m) => ({ default: m.CompareBar })));  // perf: compare bar + drawer lib off the main bundle
import { ScrollToTop } from "@/lib/ScrollToTop";
import { CustomerProvider } from "@/storefront/CustomerContext";
import NotFound from "@/storefront/NotFound";

const JobsSection = lazy(() => import("@/pages/jobs/JobsSection"));
const CatalogueHealth = lazy(() => import("@/pages/CatalogueHealth"));
const SettingsIntegrations = lazy(() => import("@/pages/SettingsIntegrations"));
const AdminConsole = lazy(() => import("@/pages/AdminConsole"));
const UserPage = lazy(() => import("@/pages/users/UserPage"));  // T35
const InviteAdmin = lazy(() => import("@/pages/users/InviteAdmin"));
const AdminProductPage = lazy(() => import("@/pages/products/ProductPage"));  // T35
const AdminCatalogue = lazy(() => import("@/pages/AdminCatalogue"));
const CustomerAuth = lazy(() => import("@/pages/CustomerAuth"));
const InvitationAccept = lazy(() => import("@/pages/InvitationAccept"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/ResetPassword").then((m) => ({ default: m.VerifyEmail })));
const CartPage = lazy(() => import("@/storefront/CartPage"));
const CheckoutPage = lazy(() => import("@/storefront/CheckoutPage"));
const OrderConfirmation = lazy(() => import("@/storefront/OrderConfirmation"));
const ComparePage = lazy(() => import("@/storefront/ComparePage"));
const CustomerOrders = lazy(() => import("@/storefront/CustomerOrders"));
const SavedLists = lazy(() => import("@/storefront/SavedLists"));
const SharedList = lazy(() => import("@/storefront/SharedList"));
const FreightQuoteReview = lazy(() => import("@/storefront/FreightQuoteReview"));
const AdminFreightQuotes = lazy(() => import("@/pages/AdminFreightQuotes"));
const AdminOrdersList = lazy(() => import("@/pages/orders/OrdersList"));
const AdminOrderDetail = lazy(() => import("@/pages/orders/OrderDetail"));
const AdminNewOrder = lazy(() => import("@/pages/orders/new/NewOrder"));
const PayLink = lazy(() => import("@/storefront/PayLink"));
const SupplierOrderSettings = lazy(() => import("@/pages/supplier-orders/SupplierOrderSettings"));  // T37
const DistributorPage = lazy(() => import("@/pages/distributors/DistributorPage"));  // T47
const SORedirect = lazy(() => import("@/pages/orders/SupplierOrderRedirect").then((m) => ({ default: m.SupplierOrderRedirect })));
const SOListRedirect = lazy(() => import("@/pages/orders/SupplierOrderRedirect").then((m) => ({ default: m.SupplierOrdersListRedirect })));
const AdminMerchandising = lazy(() => import("@/pages/AdminMerchandising"));
const ProcurementDashboard = lazy(() => import("@/storefront/ProcurementDashboard"));
const Quotes = lazy(() => import("@/storefront/Quotes"));
const Templates = lazy(() => import("@/storefront/Templates"));
const AdminProcurementTemplates = lazy(() => import("@/pages/AdminProcurementTemplates"));
const AdminQuotes = lazy(() => import("@/pages/AdminQuotes"));
const AdminSimulation = lazy(() => import("@/pages/AdminSimulation"));
const AdminDashboardLite = lazy(() => import("@/pages/AdminDashboardLite"));
const AdminShell = lazy(() => import("@/admin/AdminShell"));
const AdminPlaceholder = lazy(() => import("@/admin/Placeholder"));
const ProductNew = lazy(() => import("@/pages/products/ProductNew"));  // T37
const AdminCategories = lazy(() => import("@/pages/AdminCategories"));
const AdminBrands = lazy(() => import("@/pages/AdminBrands"));
const AdminAttributes = lazy(() => import("@/pages/AdminAttributes"));
const AdminPricing = lazy(() => import("@/pages/AdminPricing"));
const SettingsEmail = lazy(() => import("@/pages/email/SettingsEmail"));
const SettingsBranding = lazy(() => import("@/pages/SettingsBranding"));
const AdminFeatureFlags = lazy(() => import("@/pages/AdminFeatureFlags"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminContent = lazy(() => import("@/pages/AdminContent"));
const AdminStorefront = lazy(() => import("@/pages/AdminStorefront"));
const AdminCatalogueImport = lazy(() => import("@/pages/AdminCatalogueImport"));
const AdminLists = import("@/pages/lists/AdminLists");  // T33b shared list engine
const CustomersList = lazy(() => AdminLists.then((m) => ({ default: m.CustomersList })));
const DistributorsList = lazy(() => AdminLists.then((m) => ({ default: m.DistributorsList })));
const ProductsList = lazy(() => AdminLists.then((m) => ({ default: m.ProductsList })));
const AdminLists2 = import("@/pages/lists/AdminLists2");  // T34 record lists
const QuotesList = lazy(() => AdminLists2.then((m) => ({ default: m.QuotesList })));
const FreightQuotesList = lazy(() => AdminLists2.then((m) => ({ default: m.FreightQuotesList })));
const ExceptionsList = lazy(() => AdminLists2.then((m) => ({ default: m.ExceptionsList })));
const AuditList = lazy(() => AdminLists2.then((m) => ({ default: m.AuditList })));
const AdminUsersList = lazy(() => AdminLists2.then((m) => ({ default: m.AdminUsersList })));
const MediaLibrary = lazy(() => import("./pages/media/MediaLibrary"));
const DiscontinuedPage = lazy(() => import("./pages/products/DiscontinuedPage"));  // T44  // T40 WordPress-style library
const CustomerPage = lazy(() => import("@/pages/customers/CustomerPage"));
const AdminProductDetail = lazy(() => import("@/pages/AdminProductDetail"));  // eslint-disable-line no-unused-vars
const PolicyPage = lazy(() => import("@/storefront/PolicyPage"));
const NotificationPreferences = lazy(() => import("@/storefront/NotificationPreferences"));
const StockAlerts = lazy(() => import("@/storefront/StockAlerts"));
const AdminPurchaseStates = lazy(() => import("@/pages/AdminPurchaseStates"));
const AdminBundleImages = lazy(() => import("@/pages/AdminBundleImages"));
const AdminAssistant = lazy(() => import("@/pages/AdminAssistant"));
const AdminProductInfoReports = lazy(() => import("@/pages/AdminProductInfoReports"));
const AdminProductTypes = lazy(() => import("@/pages/AdminProductTypes"));
const StoreAuth = lazy(() => import("@/storefront/StoreAuth"));
const Account = lazy(() => import("@/storefront/Account"));

// T10.b: old URLs -> new homes
function ParamRedirect({ to }) {
  const p = useParams();
  return <Navigate to={to.replace(/:(\w+)/g, (_, k) => p[k])} replace />;
}
// Former "classic view" URLs -> the new page, keeping ?q= etc; #invite -> the invite page.
function QueryRedirect({ to }) {
  const { search, hash } = useLocation();
  const p = new URLSearchParams(search);
  p.delete("classic");
  const q = p.toString();
  if (hash === "#invite") return <Navigate to="/admin/users-roles/invite" replace />;
  if (to === "/admin/products") {  // old Products page: ?new=1 -> create page, a product id -> its page
    const id = p.get("open") || p.get("id") || p.get("product");
    if (p.get("new")) return <Navigate to="/admin/products/new" replace />;
    if (id) return <Navigate to={`/admin/catalogue/product/${encodeURIComponent(id)}`} replace />;
  }
  return <Navigate to={`${to}${q ? `?${q}` : ""}`} replace />;
}
function OpsToolRedirect() {
  const { tool } = useParams();
  const to = { "stuck-jobs": "/admin/operations/jobs/running", "enrichment-sample": "/admin/catalogue/health/icecat-runs" }[tool] || `/admin/operations/jobs/maintenance/${tool}`;
  return <Navigate to={to} replace />;
}

loadBrand();

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <CustomerProvider>
        <ScrollToTop />
        <Suspense fallback={null}><CompareBar /></Suspense>
        <Suspense fallback={<div data-testid="route-loading" style={{ minHeight: "100vh" }} />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<SearchPage />} />
          <Route path="/c/:slug" element={<CategoryPage />} />
          <Route path="/b/:slug" element={<ParamRedirect to="/brands/:slug" />} />
          <Route path="/brands" element={<BrandsIndex />} />
          <Route path="/brands/:slug" element={<BrandPage />} />
          <Route path="/p/:slug" element={<ProductPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/ex-demo-clearance" element={<ClearancePage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/account/orders" element={<CustomerOrders />} />
          <Route path="/lists" element={<SavedLists />} />
          <Route path="/lists/shared/:token" element={<SharedList />} />
          <Route path="/account/procurement" element={<ProcurementDashboard />} />
          <Route path="/procurement" element={<ProcurementDashboard />} />
          <Route path="/procurement/templates" element={<Templates />} />
          <Route path="/procurement/templates/:slug" element={<Templates />} />
          <Route path="/account/quotes" element={<Quotes />} />
          <Route path="/account/quotes/:reference" element={<Quotes />} />
          <Route path="/admin" element={<AdminShell><AdminDashboardLite /></AdminShell>} />
          <Route path="/admin/procurement-templates" element={<AdminShell><AdminProcurementTemplates /></AdminShell>} />
          <Route path="/admin/quotes" element={<AdminShell><QuotesList /></AdminShell>} />
          <Route path="/admin/quotes/classic" element={<QueryRedirect to="/admin/quotes" />} />
          <Route path="/admin/quotes/:reference" element={<AdminShell><AdminQuotes /></AdminShell>} />
          <Route path="/admin/launch-readiness" element={<Navigate to="/admin/settings/integrations/launch" replace />} />
          <Route path="/admin/supplier-orders" element={<AdminShell><SOListRedirect /></AdminShell>} />
          <Route path="/admin/simulation" element={<AdminShell><AdminSimulation /></AdminShell>} />
          <Route path="/policies/:slug" element={<PolicyPage />} />
          <Route path="/account/notifications" element={<NotificationPreferences />} />
          <Route path="/account/stock-alerts" element={<StockAlerts />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/orders/access-invalid" element={<OrderConfirmation invalid />} />
          <Route path="/orders/:reference" element={<OrderConfirmation />} />
          <Route path="/checkout/freight-quote/review" element={<FreightQuoteReview />} />
          <Route path="/checkout/freight-quote/accept" element={<FreightQuoteReview />} />
          <Route path="/admin/freight-quotes" element={<AdminShell><FreightQuotesList /></AdminShell>} />
          <Route path="/admin/freight-quotes/classic" element={<QueryRedirect to="/admin/freight-quotes" />} />
          <Route path="/admin/freight-quotes/:id" element={<AdminShell><AdminFreightQuotes /></AdminShell>} />
          <Route path="/customer/auth" element={<CustomerAuth />} />
          <Route path="/account/auth" element={<StoreAuth />} />
          <Route path="/account" element={<Account />} />
          {/* /customer/* aliases - map to the same authenticated storefront pages so
              these URLs never render a blank container. */}
          <Route path="/customer" element={<Navigate to="/account" replace />} />
          <Route path="/customer/orders" element={<CustomerOrders />} />
          <Route path="/customer/quotes" element={<Quotes />} />
          <Route path="/customer/quotes/:reference" element={<Quotes />} />
          <Route path="/customer/saved-lists" element={<SavedLists />} />
          <Route path="/customer/lists" element={<SavedLists />} />
          <Route path="/customer/procurement" element={<ProcurementDashboard />} />
          <Route path="/customer/procurement-templates" element={<Templates />} />
          <Route path="/admin/orders" element={<AdminShell><AdminOrdersList /></AdminShell>} />
          <Route path="/admin/merchandising" element={<AdminShell><AdminMerchandising /></AdminShell>} />
          <Route path="/admin/orders/supplier-orders" element={<AdminShell><SOListRedirect /></AdminShell>} />
          <Route path="/admin/orders/supplier-orders/classic" element={<AdminShell><SOListRedirect /></AdminShell>} />
          <Route path="/admin/orders/supplier-orders/:id" element={<AdminShell><SORedirect /></AdminShell>} />
          <Route path="/admin/orders/new" element={<AdminShell><AdminNewOrder /></AdminShell>} />
          <Route path="/admin/orders/:reference" element={<AdminShell><AdminOrderDetail /></AdminShell>} />
          <Route path="/pay/:token" element={<PayLink />} />
          <Route path="/admin/console" element={<AdminConsole />} />
          <Route path="/admin/login" element={<AdminConsole />} />
          <Route path="/admin/diagnostics" element={<AdminConsole />} />
          <Route path="/admin/change-password" element={<AdminConsole />} />
          <Route path="/admin/mfa/enrol" element={<AdminConsole />} />
          <Route path="/admin/mfa/verify" element={<AdminConsole />} />
          <Route path="/admin/admins" element={<Navigate to="/admin/users-roles" replace />} />
          <Route path="/admin/users-roles" element={<AdminShell><AdminUsersList /></AdminShell>} />
          <Route path="/admin/users-roles/classic" element={<QueryRedirect to="/admin/users-roles" />} />
          <Route path="/admin/users-roles/invite" element={<AdminShell><InviteAdmin /></AdminShell>} />
          <Route path="/admin/users-roles/:id" element={<AdminShell><UserPage /></AdminShell>} />
          <Route path="/admin/catalogue" element={<AdminShell><AdminCatalogue /></AdminShell>} />
          <Route path="/admin/catalogue/import" element={<AdminShell><AdminCatalogueImport /></AdminShell>} />
          <Route path="/admin/exceptions" element={<AdminShell><ExceptionsList /></AdminShell>} />
          <Route path="/admin/exceptions/classic" element={<QueryRedirect to="/admin/exceptions" />} />
          <Route path="/admin/jobs" element={<Navigate to="/admin/operations/jobs" replace />} />
          <Route path="/admin/system/operations" element={<Navigate to="/admin/operations/jobs/maintenance" replace />} />
          <Route path="/admin/system/operations/:tool" element={<OpsToolRedirect />} />
          <Route path="/admin/jobs/:jobType" element={<ParamRedirect to="/admin/operations/jobs/definitions/:jobType" />} />
          <Route path="/admin/job-runs/:runId" element={<ParamRedirect to="/admin/operations/jobs/runs/:runId" />} />
          <Route path="/admin/products" element={<AdminShell><ProductsList /></AdminShell>} />
          <Route path="/admin/products/classic" element={<QueryRedirect to="/admin/products" />} />
          <Route path="/admin/products/new" element={<AdminShell><ProductNew /></AdminShell>} />
          <Route path="/admin/categories" element={<AdminShell><AdminCategories /></AdminShell>} />
          <Route path="/admin/brands" element={<AdminShell><AdminBrands /></AdminShell>} />
          <Route path="/admin/brands/logo-matches" element={<AdminShell><AdminBrandLogoMatches /></AdminShell>} />
          <Route path="/admin/attributes" element={<AdminShell><AdminAttributes /></AdminShell>} />
          <Route path="/admin/pricing" element={<AdminShell><AdminPricing /></AdminShell>} />
          <Route path="/admin/availability" element={<AdminShell><AdminPurchaseStates /></AdminShell>} />
          <Route path="/admin/catalogue/bundle-images" element={<AdminShell><AdminBundleImages /></AdminShell>} />
          <Route path="/admin/catalogue/product-info-reports" element={<AdminShell><AdminProductInfoReports /></AdminShell>} />
          <Route path="/admin/catalogue/product-types" element={<AdminShell><AdminProductTypes /></AdminShell>} />
          <Route path="/admin/content/help-me-choose" element={<AdminShell><AdminAssistant /></AdminShell>} />
          <Route path="/admin/audit" element={<AdminShell><AuditList /></AdminShell>} />
          <Route path="/admin/audit/classic" element={<QueryRedirect to="/admin/audit" />} />
          <Route path="/admin/integrations" element={<Navigate to="/admin/settings/integrations/health" replace />} />
          <Route path="/admin/email" element={<Navigate to="/admin/settings/email/log" replace />} />
          <Route path="/admin/settings/email" element={<AdminShell><SettingsEmail /></AdminShell>} />
          <Route path="/admin/settings/email/:tab" element={<AdminShell><SettingsEmail /></AdminShell>} />
          <Route path="/admin/settings/supplier" element={<Navigate to="/admin/settings/integrations/dicker" replace />} />
          <Route path="/admin/cancellations" element={<Navigate to="/admin/orders?v=builtin:cancellation_requests" replace />} />  {/* T33b: now an Orders System View */}
          <Route path="/admin/cancellation-requests" element={<Navigate to="/admin/orders?v=builtin:cancellation_requests" replace />} />  {/* T38: menu item removed */}
          <Route path="/admin/media" element={<AdminShell><MediaLibrary /></AdminShell>} />
          <Route path="/admin/products/discontinued" element={<AdminShell><DiscontinuedPage /></AdminShell>} />
          <Route path="/admin/media/classic" element={<QueryRedirect to="/admin/media" />} />
          <Route path="/admin/settings/distributor-ordering-rules" element={<AdminShell><SupplierOrderSettings /></AdminShell>} />
          <Route path="/admin/settings/supplier-orders" element={<Navigate to="/admin/settings/distributor-ordering-rules" replace />} />
          <Route path="/admin/settings/distributors" element={<AdminShell><DistributorsList /></AdminShell>} />
          <Route path="/admin/settings/distributors/new" element={<AdminShell><DistributorPage /></AdminShell>} />
          <Route path="/admin/settings/distributors/:id" element={<AdminShell><DistributorPage /></AdminShell>} />
          <Route path="/admin/settings/feature-flags" element={<AdminShell><AdminFeatureFlags /></AdminShell>} />
          <Route path="/admin/settings/store" element={<AdminShell><AdminSettings /></AdminShell>} />
          <Route path="/admin/settings/branding" element={<AdminShell><SettingsBranding /></AdminShell>} />
          <Route path="/admin/settings/gst" element={<AdminShell><AdminSettings /></AdminShell>} />
          <Route path="/admin/settings/freight" element={<AdminShell><AdminSettings /></AdminShell>} />
          <Route path="/admin/settings/notifications" element={<Navigate to="/admin/settings/email/configuration" replace />} />
          <Route path="/admin/settings/seo" element={<AdminShell><AdminSettings /></AdminShell>} />
          <Route path="/admin/settings/status-mapping" element={<AdminShell><AdminSettings /></AdminShell>} />
          <Route path="/admin/content/pages" element={<AdminShell><AdminContent /></AdminShell>} />
          <Route path="/admin/content/help" element={<AdminShell><AdminContent /></AdminShell>} />
          <Route path="/admin/content/policies" element={<AdminShell><AdminContent /></AdminShell>} />
          <Route path="/admin/content/homepage-tiles" element={<AdminShell><AdminStorefront /></AdminShell>} />
          <Route path="/admin/content/homepage-highlights" element={<AdminShell><AdminStorefront /></AdminShell>} />
          <Route path="/admin/content/homepage-promo" element={<AdminShell><AdminStorefront /></AdminShell>} />
          <Route path="/admin/content/homepage-banner" element={<AdminShell><AdminStorefront /></AdminShell>} />
          <Route path="/admin/content/footer" element={<AdminShell><AdminStorefront /></AdminShell>} />
          <Route path="/admin/content/navigation" element={<AdminShell><AdminStorefront /></AdminShell>} />
          <Route path="/admin/customers" element={<AdminShell><CustomersList /></AdminShell>} />
          <Route path="/admin/customers/classic" element={<QueryRedirect to="/admin/customers" />} />
          <Route path="/admin/customers/:id" element={<AdminShell><CustomerPage /></AdminShell>} />
          <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/catalogue/products" element={<Navigate to="/admin/products" replace />} />
          <Route path="/admin/catalogue/categories" element={<Navigate to="/admin/categories" replace />} />
          <Route path="/admin/catalogue/brand-aliases" element={<Navigate to="/admin/catalogue/health/aliases" replace />} />
          <Route path="/admin/catalogue/icecat-match-review" element={<Navigate to="/admin/catalogue/health/review" replace />} />
          <Route path="/admin/catalogue/missing-images" element={<Navigate to="/admin/catalogue/health/images" replace />} />
          <Route path="/admin/catalogue/data-quality" element={<Navigate to="/admin/catalogue/health/data-quality" replace />} />
          <Route path="/admin/catalogue/product/:id" element={<AdminShell><AdminProductPage /></AdminShell>} />
          <Route path="/admin/integrations/icecat" element={<Navigate to="/admin/settings/integrations/icecat" replace />} />
          <Route path="/admin/operations/jobs/*" element={<AdminShell><JobsSection /></AdminShell>} />
          <Route path="/admin/catalogue/health" element={<AdminShell><CatalogueHealth /></AdminShell>} />
          <Route path="/admin/catalogue/health/:tab" element={<AdminShell><CatalogueHealth /></AdminShell>} />
          <Route path="/admin/settings/integrations" element={<AdminShell><SettingsIntegrations /></AdminShell>} />
          <Route path="/admin/settings/integrations/:tab" element={<AdminShell><SettingsIntegrations /></AdminShell>} />
          <Route path="/admin/email-outbox" element={<Navigate to="/admin/settings/email/log" replace />} />
          <Route path="/admin/readiness" element={<Navigate to="/admin/settings/integrations/launch" replace />} />
          <Route path="/admin/cancellations-amendments" element={<Navigate to="/admin/orders?v=builtin:cancellation_requests" replace />} />
          <Route path="/admin/invitations/accept" element={<InvitationAccept />} />
          <Route path="/admin/*" element={<AdminShell><AdminPlaceholder /></AdminShell>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* Catch-all: any unmatched non-admin path renders a proper 404, never blank. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </CustomerProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
